import {Fragment} from "react";
import Section from "../../Section";
import {Grid, Stack} from "@mui/material";
import {defineMessage, FormattedMessage} from "react-intl";
import {labelStyle, valueStyle} from "../../../styles";
import BackendError from "../../BackendError";
import {ThreeDots} from "@dvrt/graphql-widgets";
import DeviceSummary from "./DeviceSummary";
import {useExtensionBasicInfoQuery} from "../queries.generated";

type Props = {
    extensionId: number;
};

export default ({extensionId}: Props) => {
    const {loading, data, error} = useExtensionBasicInfoQuery({
        variables: {userId: extensionId},
        fetchPolicy: "cache-and-network",
    });

    if (loading) return <ThreeDots height={5} />;

    if (error) return <BackendError error={error} />;

    const tooltipTitle = (
        <Fragment>
            <FormattedMessage id="total" defaultMessage="Total" /> /{" "}
            <FormattedMessage id="not_registered" defaultMessage="Not registered" />
        </Fragment>
    );

    return (
        <Stack spacing={3}>
            <Section label={defineMessage({id: "basic_info", defaultMessage: "Basic info"})}>
                <Grid container>
                    <Grid style={labelStyle} size={2}>
                        <span>
                            <FormattedMessage id="displayName" defaultMessage="Name" />:
                        </span>
                    </Grid>
                    <Grid style={valueStyle} size={2}>
                        <span>{data.userBasicInfo.displayName}</span>
                    </Grid>
                </Grid>
                <Grid container>
                    <Grid style={labelStyle} size={2}>
                        <span>
                            <FormattedMessage id="shortNumber" defaultMessage="Short number" />:
                        </span>
                    </Grid>
                    <Grid style={valueStyle} size={2}>
                        <span>{data.userBasicInfo.shortNumber}</span>
                    </Grid>
                </Grid>
                <Grid container>
                    <Grid style={labelStyle} size={2}>
                        <span>
                            <FormattedMessage id="e164" defaultMessage="E.164" />:
                        </span>
                    </Grid>
                    <Grid style={valueStyle} size={2}>
                        <span>{data.userBasicInfo.e164}</span>
                    </Grid>
                </Grid>
            </Section>
            <Section label={defineMessage({id: "device_summary", defaultMessage: "Device summary"})}>
                {Boolean(data.userBasicInfo.devicesClient) && (
                    <DeviceSummary
                        label={defineMessage({id: "CLIENT", defaultMessage: "Client"})}
                        tooltipTitle={tooltipTitle}
                        all={data.userBasicInfo.devicesClient}
                        notRegistered={data.userBasicInfo.devicesClientNotRegistered}
                    />
                )}
                {Boolean(data.userBasicInfo.devicesLinked) && (
                    <DeviceSummary
                        label={defineMessage({id: "LINKED", defaultMessage: "Linked"})}
                        tooltipTitle={tooltipTitle}
                        all={data.userBasicInfo.devicesLinked}
                        notRegistered={data.userBasicInfo.devicesLinkedNotRegistered}
                    />
                )}
                {Boolean(data.userBasicInfo.devicesProvisioned) && (
                    <DeviceSummary
                        label={defineMessage({id: "PROVISIONED", defaultMessage: "Provisioned"})}
                        tooltipTitle={tooltipTitle}
                        all={data.userBasicInfo.devicesProvisioned}
                        notRegistered={data.userBasicInfo.devicesProvisionedNotRegistered}
                    />
                )}
                {Boolean(data.userBasicInfo.devicesThirdparty) && (
                    <DeviceSummary
                        label={defineMessage({id: "THIRDPARTY", defaultMessage: "Third-Party"})}
                        tooltipTitle={tooltipTitle}
                        all={data.userBasicInfo.devicesThirdparty}
                        notRegistered={data.userBasicInfo.devicesThirdpartyNotRegistered}
                    />
                )}
                {data.userBasicInfo.devicesThirdparty +
                    data.userBasicInfo.devicesClient +
                    data.userBasicInfo.devicesProvisioned +
                    data.userBasicInfo.devicesLinked ===
                    0 && (
                    <Grid container>
                        <span>
                            <FormattedMessage
                                id="extension.there_is_no_device"
                                defaultMessage="There is no device belonging to this user"
                            />
                        </span>
                    </Grid>
                )}
            </Section>
        </Stack>
    );
};
